/**
 * zoo.ts — land + building + breeding + building interior placements
 * (PR-15 split of api/client.ts).
 *
 * The zoo system is the "real estate" expansion: kid buys lands, places
 * buildings on them, assigns pets to buildings, breeds pets, and
 * decorates interiors. All of that is one cohesive backend namespace
 * (`/api/v1/child/zoo/*`) so it gets one file.
 *
 * 调用关系：
 *   - Merged into the unified `api` object via api/client.ts.
 *   - Used by ZooPage, BuildingInteriorPage, LandShopSection, and
 *     scenes/pixi/ZooScene (consumes the typed shapes ZooOverview/ZooPet).
 *   - Backend: backend/src/routes/zoo.js, breeding.js, building_interior.js.
 *
 * 关键约束：
 *   - saveBuildingInterior is destructive on the server — it replaces all
 *     placements for that building. Callers must send the full desired
 *     placement list, not a diff.
 */
import { call } from './_core';

export interface LandType {
  id: number; code: string; display_name: string; description: string | null;
  price: number; capacity: number; sprite_key: string | null;
  unlock_level: number;
}

export interface BuildingType {
  id: number; code: string; display_name: string; description: string | null;
  price: number; capacity: number; species_allow: string | null; sprite_key: string | null;
  unlock_level: number;
}

export interface ZooLand {
  id: number; land_type_id: number; nickname: string | null;
  purchased_at: number; code: string; display_name: string; capacity: number;
  sprite_key: string | null;
}

export interface ZooBuilding {
  id: number; land_id: number; building_type_id: number; nickname: string | null;
  level: number; built_at: number;
  code: string; display_name: string; capacity: number;
  species_allow: string | null; sprite_key: string | null;
}

export interface ZooPet {
  id: number; nickname: string; state: string; building_id: number | null;
  adopted_at: number; species: string; breed_name: string; rarity: string;
  sprite_key: string | null;
  metadata: string | null;
}

export interface ZooBreeding {
  id: number; parent_a_id: number; parent_b_id: number;
  started_at: number; complete_at: number;
  claimed_at: number | null; offspring_pet_id: number | null;
}

export interface ZooOverview {
  land_types: LandType[];
  building_types: BuildingType[];
  lands: ZooLand[];
  buildings: ZooBuilding[];
  pets: ZooPet[];
  breedings: ZooBreeding[];
}

export const zooApi = {
  // Zoo
  zooOverview() {
    return call<ZooOverview>('/api/v1/child/zoo/overview');
  },
  buyLand(land_type_id: number, nickname?: string) {
    return call<{ ok: true; land_id: number; balance_after: number }>(
      '/api/v1/child/zoo/lands/buy',
      { method: 'POST', body: JSON.stringify({ land_type_id, nickname }) },
    );
  },
  buildBuilding(land_id: number, building_type_id: number, nickname?: string) {
    return call<{ ok: true; building_id: number; balance_after: number }>(
      '/api/v1/child/zoo/buildings/build',
      { method: 'POST', body: JSON.stringify({ land_id, building_type_id, nickname }) },
    );
  },
  assignPetBuilding(pet_id: number, building_id: number | null) {
    return call<{ ok: true }>(
      `/api/v1/child/zoo/pets/${pet_id}/building`,
      { method: 'PUT', body: JSON.stringify({ building_id }) },
    );
  },
  startBreeding(parent_a_id: number, parent_b_id: number) {
    return call<{ ok: true; breeding_id: number; complete_at: number }>(
      '/api/v1/child/zoo/breeding/start',
      { method: 'POST', body: JSON.stringify({ parent_a_id, parent_b_id }) },
    );
  },
  claimBreeding(id: number) {
    return call<{ ok: true; baby: { id: number; nickname: string; breed_name: string; species: string } }>(
      `/api/v1/child/zoo/breeding/${id}/claim`,
      { method: 'POST' },
    );
  },

  // Building interior — decor placements scoped to a single owned building.
  buildingInterior(buildingId: number) {
    return call<{
      building: {
        id: number;
        nickname: string | null;
        level: number;
        type_code: string;
        type_name: string;
        type_sprite: string | null;
        capacity: number;
      };
      placements: Array<{
        id: number;
        item_id: number;
        x: number;
        y: number;
        rotation: number;
        scale: number;
        z_index: number;
        code: string;
        display_name: string;
        category: string;
        sprite_key: string | null;
      }>;
    }>(`/api/v1/child/zoo/buildings/${buildingId}/interior`);
  },
  saveBuildingInterior(
    buildingId: number,
    placements: Array<{
      item_id: number;
      x: number;
      y: number;
      rotation?: number;
      scale?: number;
      z_index?: number;
    }>,
  ) {
    return call<{ ok: true; count: number }>(
      `/api/v1/child/zoo/buildings/${buildingId}/interior`,
      { method: 'PUT', body: JSON.stringify({ placements }) },
    );
  },
};
